import { useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { syncSettingsToSupabase, loadSettingsFromSupabase } from "@/lib/settingsSync";
import { saveUserSettings } from "@/lib/userSettings";
import { loadLearningFromCloud } from "@/lib/aacLearningSync";
import { getPredictionEngine } from "@/lib/aacPredictionEngine";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Trigger settings sync on login (use setTimeout to avoid deadlock)
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(async () => {
            try {
              // First sync localStorage to Supabase
              await syncSettingsToSupabase(session.user.id);
              
              // Then load from Supabase and merge into localStorage
              const cloudSettings = await loadSettingsFromSupabase(session.user.id);
              if (cloudSettings) {
                saveUserSettings(cloudSettings);
              }

              // Load AAC learning data from cloud
              const cloudLearning = await loadLearningFromCloud(session.user.id);
              if (cloudLearning) {
                const engine = getPredictionEngine();
                engine.setCloudData(cloudLearning);
              }
            } catch (error) {
              console.error("[useAuth] Settings sync error:", error);
            }
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Also sync on initial load if user is already logged in
      if (session?.user) {
        setTimeout(async () => {
          try {
            await syncSettingsToSupabase(session.user.id);
            const cloudSettings = await loadSettingsFromSupabase(session.user.id);
            if (cloudSettings) {
              saveUserSettings(cloudSettings);
            }

            // Load AAC learning data from cloud
            const cloudLearning = await loadLearningFromCloud(session.user.id);
            if (cloudLearning) {
              const engine = getPredictionEngine();
              engine.setCloudData(cloudLearning);
            }
          } catch (error) {
            console.error("[useAuth] Initial settings sync error:", error);
          }
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { user, session, loading, signOut };
}
